import { EffectType } from "@1games-ru/region-shared/dist/enums/effect/effect";
import { isNumber, isUndefined } from "lodash";
import { motion } from "motion/react";
import { FC, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";
import { cn } from "@/core/helpers/cn";
import {
  BicepsIcon,
  DropPulseIcon,
  EasyHungryIcon,
  HardHungryIcon,
  MedPatchIcon,
  MedPillIcon,
  WineIcon,
  ZapIcon,
} from "@/shared/icons";
import { BaseIconProps } from "@/shared/icons/BaseIcon";
import { percentByPeriod } from "@/domains/hud/helpers/percentByPeriod";
import LinearIndicator from "@/domains/hud/components/indicator/LinearIndicator";

type Props = {
  className?: string;
  effect: EffectType;
  startedAt?: number;
  finishedAt?: number;
};

const icons: Record<EffectType, FC<BaseIconProps>> = {
  [EffectType.Bleeding]: DropPulseIcon,
  [EffectType.Injury]: MedPatchIcon,
  [EffectType.Regeneration]: MedPillIcon,
  [EffectType.WeakHunger]: EasyHungryIcon,
  [EffectType.StrongHunger]: HardHungryIcon,
  [EffectType.EnergyBoost]: ZapIcon,
  [EffectType.WellFed]: BicepsIcon,
  [EffectType.Drunk]: WineIcon,
};

export default function HudEffectType({ className, effect, startedAt, finishedAt }: Props) {
  const hasFinish = isNumber(finishedAt);
  const { totalSeconds } = useTimer({
    expiryTimestamp: hasFinish ? new Date(finishedAt * 1000) : new Date(),
    autoStart: hasFinish,
  });
  const [percent, setPercent] = useState<undefined | number>(undefined);

  useEffect(() => {
    if (startedAt && finishedAt) {
      setPercent(100 - percentByPeriod(startedAt * 1000, finishedAt * 1000));
    } else {
      setPercent(undefined);
    }
  }, [startedAt, finishedAt, totalSeconds]);

  return (
    <motion.div
      layout
      className={cn(
        "flex items-center justify-center w-[2.625rem] aspect-square shrink-0 bg-dark-900/60 rounded-[0.5rem]",
        className,
      )}
      initial={{ opacity: 0, scale: 0.8, y: 6 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 6 }}
      transition={{ type: "spring", stiffness: 260, damping: 20, mass: 0.6 }}
    >
      <div className="relative w-[2.375rem] h-[2.375rem] overflow-hidden flex items-center justify-center rounded-[inherit]">
        {icons?.[effect]({ size: 20, className: "text-white" })}
        {!isUndefined(percent) && (
          <div className="absolute bottom-0 w-full">
            <LinearIndicator percent={percent} className="w-full h-[3px]" />
          </div>
        )}
      </div>
    </motion.div>
  );
}
